<template>
  <div class="subscription-payment-view">
    <div class="container">
      <AppHeader />

      <!-- Loading State -->
      <LoadingSpinner
        v-if="isInitializing"
        text="Loading payment information..."
        size="lg"
      />

      <!-- Main Content -->
      <template v-else-if="reference">
        <!-- Payment Success -->
        <PaymentSuccess
          v-if="reference.processed"
          :reference="reference"
        />

        <!-- Payment Flow -->
        <template v-else>
          <CustomerInformation :reference="reference" />

          <!-- Payment Error Alert -->
          <div v-if="paymentError" class="payment-error-alert">
            <div class="alert-content">
              <svg class="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span class="alert-message">{{ paymentError }}</span>
            </div>
            <button type="button" class="alert-dismiss" @click="paymentError = null">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- Payment Method Selection -->
          <PaymentMethodSelector
            v-if="!selectedPaymentMethod"
            :is-loading="isLoading"
            :loading-method="loadingMethod"
            :amount="reference.amount"
            :last-payment-method="reference.payment_type"
            @select-method="handleMethodSelect"
          />

          <!-- Selected Payment Method -->
          <template v-else>
            <!-- Payment Method Header with Change Button -->
            <SelectedPaymentHeader
              :selected-method="selectedPaymentMethod"
              :disabled="isProcessingVcd || isLoadingAccounts"
              @change="resetPaymentMethod"
            />

            <EftPaymentDetails
              v-if="selectedPaymentMethod === PAYMENT_METHODS.EFT"
              :payment-reference="reference.payment_reference"
            />

            <KazangMobileMoney
              v-if="selectedPaymentMethod === PAYMENT_METHODS.KAZANG_MOBILE"
              :reference="reference"
              :reference-id="ref"
            />

            <KazangVcdSelector
              v-if="selectedPaymentMethod === PAYMENT_METHODS.KAZANG_VCD"
              :accounts="kazangAccounts"
              :is-loading="isLoadingAccounts"
              :is-processing="isProcessingVcd"
              @select-account="handleVcdPayment"
            />

            <DpoPayment
              v-if="selectedPaymentMethod === PAYMENT_METHODS.DPO"
              :reference="reference"
              :reference-id="ref"
            />
          </template>
        </template>
      </template>

      <!-- Missing Reference State -->
      <div v-else-if="errorType === 'missing'" class="error-state">
        <div class="error-card">
          <div class="error-icon error-icon--warning">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h3>Payment Reference Required</h3>
          <p>No payment reference was provided. Please use the link from your invoice or payment notification to access this page.</p>
          <div class="error-help">
            <span class="help-label">Need help?</span>
            <a href="https://chrilantech.com" target="_blank" rel="noopener" class="help-link">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      <!-- Invalid Reference State -->
      <div v-else-if="errorType === 'invalid'" class="error-state">
        <div class="error-card">
          <div class="error-icon error-icon--error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h3>Invalid Payment Reference</h3>
          <p>The payment reference provided is invalid or has expired. Please check your link and try again.</p>
          <div class="error-details">
            <span class="details-label">Reference:</span>
            <code class="details-value">{{ ref }}</code>
          </div>
          <div class="error-help">
            <span class="help-label">Need help?</span>
            <a href="https://chrilantech.com" target="_blank" rel="noopener" class="help-link">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      <!-- Network/Server Error State -->
      <div v-else-if="errorType === 'network'" class="error-state">
        <div class="error-card">
          <div class="error-icon error-icon--error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h3>Connection Error</h3>
          <p>{{ loadError }}</p>
          <button type="button" class="retry-btn" @click="initPage">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="23 4 23 10 17 10"></polyline>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
            Try Again
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PAYMENT_METHODS } from '@/constants/paymentMethods'
import subscriptionService from '@/services/subscriptionService'
import paymentService from '@/services/paymentService'
import { responses } from '@/global-variables/responses'

import AppHeader from '@/components/layout/AppHeader.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import CustomerInformation from '@/components/subscription/CustomerInformation.vue'
import PaymentMethodSelector from '@/components/subscription/PaymentMethodSelector.vue'
import PaymentSuccess from '@/components/subscription/PaymentSuccess.vue'
import SelectedPaymentHeader from '@/components/subscription/SelectedPaymentHeader.vue'
import EftPaymentDetails from '@/components/subscription/payment-methods/EftPaymentDetails.vue'
import KazangMobileMoney from '@/components/subscription/payment-methods/KazangMobileMoney.vue'
import KazangVcdSelector from '@/components/subscription/payment-methods/KazangVcdSelector.vue'
import DpoPayment from '@/components/subscription/payment-methods/DpoPayment.vue'

export default {
  name: 'SubscriptionPaymentView',

  components: {
    AppHeader,
    LoadingSpinner,
    CustomerInformation,
    PaymentMethodSelector,
    PaymentSuccess,
    SelectedPaymentHeader,
    EftPaymentDetails,
    KazangMobileMoney,
    KazangVcdSelector,
    DpoPayment
  },

  data() {
    return {
      // Constants
      PAYMENT_METHODS,

      // Route params
      ref: this.$route.query.reference,

      // State
      reference: null,
      selectedPaymentMethod: null,
      kazangAccounts: [],

      // Loading states
      isInitializing: true,
      isLoading: false,
      loadingMethod: null,
      isLoadingAccounts: false,
      isProcessingVcd: false,

      // Error state
      loadError: null,
      errorType: null,  // 'missing' | 'invalid' | 'network' | null
      paymentError: null  // Dismissable error message for failed payments
    }
  },

  beforeMount() {
    this.initPage()
  },

  methods: {
    async initPage() {
      // Check if returning from Kazang payment gateway
      if (this.$route.query.ClientRef) {
        return this.handleKazangReturn()
      }

      // Check if returning from DPO payment gateway
      if (this.$route.query.dpo) {
        return this.handleDpoReturn()
      }

      this.isInitializing = true
      this.loadError = null
      this.errorType = null

      // Check for missing reference
      if (!this.ref) {
        this.errorType = 'missing'
        this.isInitializing = false
        return
      }

      try {
        await Promise.all([
          this.fetchReferenceData(),
          this.markAsViewed()
        ])
      } catch (err) {
        // Determine error type based on response
        if (
          err.isInvalidReference ||
          (err.response && (err.response.status === 404 || err.response.status === 400))
        ) {
          this.errorType = 'invalid'
        } else {
          this.errorType = 'network'
          this.loadError = 'Could not load payment information. Please check your connection and try again.'
        }
        window.errorHandler.tomcatError(err)
      } finally {
        this.isInitializing = false
      }
    },

    async fetchReferenceData() {
      const data = await subscriptionService.getReferenceData(this.ref)

      // Check if response is empty or invalid
      if (!data || Object.keys(data).length === 0 || !data.debtor_id) {
        const error = new Error('Invalid reference')
        error.isInvalidReference = true
        throw error
      }

      this.reference = data

      // Don't auto-restore payment method - always show selection so user can change their mind
    },

    async markAsViewed() {
      try {
        await subscriptionService.markAsViewed(this.ref)
      } catch (err) {
        // Non-critical, don't block the flow
        console.warn('Could not mark as viewed:', err)
      }
    },

    async handleMethodSelect(method) {
      this.loadingMethod = method
      this.isLoading = true

      try {
        switch (method) {
          case PAYMENT_METHODS.EFT:
            await this.initiateEftPayment()
            break

          case PAYMENT_METHODS.KAZANG_MOBILE:
            // No pre-fetch needed, component handles redirect
            this.selectedPaymentMethod = method
            break

          case PAYMENT_METHODS.KAZANG_VCD:
            await this.fetchKazangAccounts()
            this.selectedPaymentMethod = method
            break

          case PAYMENT_METHODS.DPO:
            // No pre-fetch needed, component handles redirect
            this.selectedPaymentMethod = method
            break
        }
      } catch (err) {
        window.errorHandler.tomcatError(err)
      } finally {
        this.isLoading = false
        this.loadingMethod = null
      }
    },

    async initiateEftPayment() {
      const data = await paymentService.initiateEftPayment(this.ref)
      this.reference = data
      this.selectedPaymentMethod = PAYMENT_METHODS.EFT
    },

    async fetchKazangAccounts() {
      this.isLoadingAccounts = true
      try {
        this.kazangAccounts = await paymentService.getKazangAccounts(this.reference.debtor_id)
      } finally {
        this.isLoadingAccounts = false
      }
    },

    async handleVcdPayment(account) {
      this.isProcessingVcd = true

      try {
        await paymentService.processVcdPayment(this.ref, account.kazangID, account.storeDB)
        responses.showSuccess('Payment Successful!')

        // Reload to show success state
        this.$router.go(0)
      } catch (err) {
        window.errorHandler.tomcatError(err)
      } finally {
        this.isProcessingVcd = false
      }
    },

    async handleKazangReturn() {
      this.isInitializing = true
      const clientRef = this.$route.query.ClientRef
      const kazangRef = this.$route.query.KazangRef

      this.ref = clientRef

      try {
        // Fetch reference data first
        await this.fetchReferenceData()

        // If already processed, just show success
        if (this.reference.processed) {
          this.isInitializing = false
          return
        }

        // Process the manual payment
        await paymentService.processManualPayment({
          id: clientRef,
          payment_reference: kazangRef,
          payment_type: 'Kazang'
        })

        // Redirect to clean URL and reload
        this.$router.replace({ path: '/', query: { reference: clientRef } })
          .then(() => window.location.reload(true))
      } catch (err) {
        window.errorHandler.tomcatError(err)
        this.isInitializing = false
      }
    },

    async handleDpoReturn() {
      this.isInitializing = true
      const dpoStatus = this.$route.query.dpo
      const errorMessage = this.$route.query.error
      const failureStatus = this.$route.query.status // "cancelled", "expired", or "failed"

      try {
        // Fetch reference data
        await this.fetchReferenceData()

        if (dpoStatus === 'success') {
          // Payment was successful, reference should be marked as processed by backend
          // If not processed yet, reload after a short delay
          if (!this.reference.processed) {
            setTimeout(() => {
              window.location.reload(true)
            }, 1000)
            return
          }
        } else if (dpoStatus === 'failed') {
          // Payment failed - show status-specific error message
          let message = errorMessage ? decodeURIComponent(errorMessage) : null

          if (!message) {
            switch (failureStatus) {
              case 'cancelled':
                message = 'Payment was cancelled. Please try again.'
                break
              case 'expired':
                message = 'Payment session expired. Please try again.'
                break
              default:
                message = 'Payment was not completed. Please try again.'
            }
          }

          // Show dismissable alert on UI
          this.paymentError = message

          // Reset to payment selection
          this.selectedPaymentMethod = null
        }

        // Clean the URL
        this.$router.replace({ path: '/', query: { reference: this.ref } })
      } catch (err) {
        window.errorHandler.tomcatError(err)
      } finally {
        this.isInitializing = false
      }
    },

    resetPaymentMethod() {
      this.selectedPaymentMethod = null
      this.kazangAccounts = []
    }
  }
}
</script>

<style scoped>
.subscription-payment-view {
  min-height: 100vh;
  background: #fafafa;
  padding: 1.5rem 1rem;
}

.container {
  max-width: 600px;
  margin: 0 auto;
}

/* Error State */
.error-state {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.error-card {
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 0.75rem;
  padding: 2.5rem;
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.error-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
}

.error-icon svg {
  width: 32px;
  height: 32px;
}

.error-icon--warning {
  background: #fef3c7;
  color: #d97706;
}

.error-icon--error {
  background: #fee2e2;
  color: #dc2626;
}

.error-card h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #18181b;
  margin: 0 0 0.5rem 0;
}

.error-card p {
  font-size: 0.875rem;
  color: #71717a;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
}

.error-details {
  background: #f4f4f5;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
}

.details-label {
  color: #71717a;
}

.details-value {
  font-family: monospace;
  color: #18181b;
  background: #e4e4e7;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  word-break: break-all;
}

.error-help {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  padding-top: 1rem;
  border-top: 1px solid #e4e4e7;
}

.help-label {
  color: #71717a;
}

.help-link {
  color: #0ea5e9;
  text-decoration: none;
  font-weight: 500;
}

.help-link:hover {
  text-decoration: underline;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #18181b;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #27272a;
}

@media (max-width: 576px) {
  .subscription-payment-view {
    padding: 1rem 0.75rem;
  }
}

/* Payment Error Alert */
.payment-error-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  margin-top: 1rem;
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.alert-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #dc2626;
}

.alert-message {
  font-size: 0.875rem;
  color: #b91c1c;
  line-height: 1.4;
}

.alert-dismiss {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  color: #b91c1c;
  cursor: pointer;
  transition: background 0.2s;
}

.alert-dismiss:hover {
  background: #fee2e2;
}

.alert-dismiss svg {
  width: 100%;
  height: 100%;
}
</style>
